import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Swiper, SwiperSlide} from 'swiper/react'
import 'swiper/css'
import 'swiper/css/navigation'
import {Navigation} from 'swiper/modules'
// Components
import {
    Box,
    SimpleGrid,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Partials
import ChildProduct from './partials/child-product.jsx'
import sliderVariant from '../product-scroller/partials/sliderVariant.jsx'
import LoadingSpinner from '../loading-spinner/index.jsx'
import useMonetate from '../../hooks/use-monetate.js'
import {useCustomerId, useShopperCustomersMutation} from '@salesforce/commerce-sdk-react/index.js'
import useTagManager from '../../hooks/use-tag-manager.js'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'

/**
 * ComboChildProducts Component
 * Renders all child products belonging to a combo (set) product.
 */
const ComboChildProducts = ({
    comboProduct,
    basket,
    countryISO,
    isBasketLoading,
    setIsChildLoading,
    showAddedCta = false,
    setChildProductSelection,
    childProductSelection,
    addToCart,
    parentProduct = {},
    wishlist = {},
    customer = {},
    isProductLoading
}) => {
    const monetate = useMonetate()
    const tagManager = useTagManager()
    const [wishlistAnimation, setWishlistAnimation] = useState(false)
    const [wishlistSpinner, setWishlistSpinner] = useState(false)
    const [wishlistLoading, setWishlistLoading] = useState([])
    const customerId = useCustomerId()
    const customerIdRef = useRef(null)
    const listIdRef = useRef(null)
    useEffect(() => {
        if (customerIdRef) {
            customerIdRef.current = customerId
        }
    }, [customerId])
    useEffect(() => {
        if (listIdRef) {
            listIdRef.current = wishlist?.id || null
        }
    }, [wishlist])

    /**************** Mutation Actions ****************/
    const createCustomerProductListItem = useShopperCustomersMutation(
        'createCustomerProductListItem'
    )
    const deleteCustomerProductListItem = useShopperCustomersMutation(
        'deleteCustomerProductListItem'
    )

    /**************** Action Handlers ****************/
    const waitForRefs = async () => {
        const maxRetries = 10
        const interval = 100
        let retries = 0

        return new Promise((resolve, reject) => {
            const checkRefs = () => {
                const listId = listIdRef.current
                if (customerIdRef.current === customerId && listId) {
                    resolve()
                } else if (retries < maxRetries) {
                    retries++
                    setTimeout(checkRefs, interval)
                } else {
                    reject(new Error('Timeout waiting for customerId and listId'))
                }
            }
            checkRefs()
        })
    }

    const addItemToWishlist = async (
        index,
        selectedProductId,
        selectedProductName,
        categoryName
    ) => {
        setWishlistLoading([...wishlistLoading, selectedProductId])
        monetate.setUsedWishlist()
        // TODO: This wishlist object is from an old API, we need to replace it with the new one.
        await waitForRefs()
        const listId = listIdRef.current
        setWishlistSpinner(true)

        try {
            await createCustomerProductListItem.mutate(
                {
                    parameters: {customerId, listId},
                    body: {
                        quantity: 1,
                        public: true,
                        priority: 1,
                        type: 'product',
                        productId: selectedProductId
                    }
                },
                {
                    onSuccess: () => {
                        setWishlistAnimation(index)
                        setWishlistSpinner(false)
                        setTimeout(() => {
                            setWishlistAnimation(false)
                        }, 1000)
                        tagManager.wishlistInteraction(
                            categoryName || '',
                            selectedProductId,
                            selectedProductName,
                            (wishlist?.customerProductListItems?.length || 0) + 1,
                            'add'
                        )

                        try {
                            const setProduct = parentProduct?.setProducts?.[index]

                            tagManager.trackGA4Event({
                                eventName: 'add_to_wishlist',
                                itemsAdded: [
                                    {
                                        product: setProduct
                                    }
                                ],
                                method: 'product sets',
                                currency: setProduct?.currency || 'USD',
                                listName: parentProduct?.name,
                                listId: parentProduct?.id
                            })
                        } catch (e) {
                            logger.error('GA4 add_to_wishlist failed', {
                                namespace: 'Wishlist.GA4',
                                additionalProperties: {error: e}
                            })
                        }
                    },
                    onSettled: () => {
                        setWishlistSpinner(false)
                        setWishlistLoading(wishlistLoading.filter((id) => id !== selectedProductId))
                    }
                }
            )
        } catch (error) {
            setWishlistSpinner(false)
            logger.error('Error adding product to wishlist', {
                namespace: 'ProductList.handleAddToWishlist',
                additionalProperties: {error, productId: selectedProductId}
            })
        }
    }

    const removeItemFromWishlist = async (
        index,
        selectedProductId,
        selectedProductName,
        categoryName
    ) => {
        setWishlistLoading([...wishlistLoading, selectedProductId])
        setWishlistSpinner(true)
        await waitForRefs()

        const listId = listIdRef.current
        const itemId = wishlist.customerProductListItems.find(
            (i) => i.productId === selectedProductId
        ).id
        try {
            await deleteCustomerProductListItem.mutate(
                {
                    body: {},
                    parameters: {customerId, listId, itemId}
                },
                {
                    onError: () => {
                        setWishlistSpinner(false)
                        setWishlistAnimation(false)
                    },
                    onSuccess: () => {
                        tagManager.wishlistInteraction(
                            categoryName || '',
                            selectedProductId,
                            selectedProductName,
                            (wishlist?.customerProductListItems?.length || 0) - 1,
                            'remove'
                        )
                        try {
                            const setProduct = parentProduct?.setProducts?.[index]

                            tagManager.trackGA4Event({
                                eventName: 'remove_from_wishlist',
                                itemsAdded: [
                                    {
                                        product: setProduct
                                    }
                                ],
                                method: 'product sets',
                                currency: setProduct?.currency || 'USD',
                                listName: parentProduct?.name,
                                listId: parentProduct?.id
                            })
                        } catch (e) {
                            logger.error('GA4 remove_from_wishlist failed', {
                                namespace: 'Combo.GA4',
                                additionalProperties: {error: e}
                            })
                        }
                        setWishlistSpinner(false)
                        setWishlistAnimation(false)
                    },
                    onSettled: () => {
                        setWishlistSpinner(false)
                        setWishlistLoading(wishlistLoading.filter((id) => id !== selectedProductId))
                    }
                }
            )
        } catch (error) {
            setWishlistSpinner(false)
            logger.error('Error adding product to wishlist', {
                namespace: 'ProductList.removeItemFromWishlist',
                additionalProperties: {error, productId: itemId}
            })
        }
    }
    const spaceBetween = {base: 0}
    const spaceBetweenValue = useBreakpointValue(spaceBetween)
    const hideArrows = comboProduct?.childProducts?.length < 3
    // slider settings
    const sliderConfig = sliderVariant['default']
    return (
        <Box
            className="sf-combo-products"
            layerStyle="fullPage"
            data-testid="child-combo-products"
            background="white"
            position="relative"
        >
            {wishlistSpinner && <LoadingSpinner position="fixed" top="auto" />}
            {parentProduct?.c_setOrFeatureContentAssetBody && (
                <Box
                    marginBottom="30px"
                    className="set-or-feature-content"
                    sx={{
                        p: {
                            color: 'black.100',
                            fontSize: '23px',
                            fontWeight: '400',
                            letterSpacing: '0.115px',
                            fontFamily: 'heading'
                        }
                    }}
                    dangerouslySetInnerHTML={{
                        __html: parentProduct?.c_setOrFeatureContentAssetBody
                    }}
                />
            )}

            <SimpleGrid columns={2} spacing="58px 10px" display={{base: 'none', lg: 'grid'}}>
                {comboProduct?.childProducts.map(({product: childProduct, quantity}, index) => (
                    <Box
                        key={childProduct.id}
                        className="child-product"
                        data-testid="child-product"
                        width={{base: '167px', lg: '185.5px'}}
                    >
                        <ChildProduct
                            index={index}
                            basket={basket}
                            product={childProduct}
                            setIsChildLoading={setIsChildLoading}
                            addToCart={addToCart}
                            customer={customer}
                            wishlistAnimation={wishlistAnimation}
                            wishlist={wishlist}
                            parentProduct={parentProduct}
                            showAddedCta={showAddedCta}
                            setChildProductSelection={setChildProductSelection}
                            childProductSelection={childProductSelection}
                            isProductPartOfSet
                            childOfBundleQuantity={quantity}
                            countryISO={countryISO}
                            selectedBundleParentQuantity={1} // hardcoded
                            isProductLoading={isProductLoading}
                            isBasketLoading={isBasketLoading}
                            onFavouriteToggle={(
                                toBeFavourite,
                                selectedProductId,
                                selectedProductName,
                                categoryName
                            ) => {
                                const action = toBeFavourite
                                    ? addItemToWishlist
                                    : removeItemFromWishlist
                                return action(
                                    index,
                                    selectedProductId,
                                    selectedProductName,
                                    categoryName
                                )
                            }}
                        />
                    </Box>
                ))}
            </SimpleGrid>
            <Box
                width="calc(100vw - 32px)"
                display={{base: 'block', lg: 'none'}}
                className="swiper-parent"
                sx={{
                    '.swiper': {
                        paddingRight: '20px',
                        paddingTop: '0px',
                        paddingBottom: '4px',
                        marginTop: 0,
                        position: 'static',
                        '&:hover': {
                            '.swiper-button-prev, .swiper-button-next': {
                                opacity: 1
                            }
                        }
                    },
                    '.swiper-wrapper': {
                        paddingLeft: '0px',
                        display: 'inline-flex'
                    },
                    '.swiper-slide': {
                        width: '167px',
                        marginRight: '10px',
                        '&:last-child': {
                            marginRight: hideArrows
                                ? 0
                                : {
                                      base: '20px !important',
                                      xs: '30px !important',
                                      lg: '40px !important'
                                  }
                        }
                    },
                    '.swiper-button-prev, .swiper-button-next': {
                        '--swiper-navigation-size': '45px',
                        top: '0px',
                        borderRadius: '5px',
                        boxShadow: 'none',
                        transition: 'opacity 0.3s ease'
                    },
                    '.swiper-button-prev': {
                        right: '47px',
                        transform: 'rotate(180deg)'
                    },
                    '.swiper-button-next': {
                        right: '0px',
                        left: 'initial'
                    }
                }}
            >
                <Swiper
                    navigation={!hideArrows}
                    slidesPerView={'auto'}
                    spaceBetween={spaceBetweenValue}
                    modules={[Navigation]}
                    {...sliderConfig}
                >
                    {comboProduct?.childProducts?.map(
                        ({product: childProduct, quantity}, index) => {
                            return (
                                <SwiperSlide key={childProduct?.id} className="swiper-slide-child">
                                    <ChildProduct
                                        index={index}
                                        basket={basket}
                                        product={childProduct}
                                        setIsChildLoading={setIsChildLoading}
                                        addToCart={addToCart}
                                        customer={customer}
                                        wishlistAnimation={wishlistAnimation}
                                        wishlist={wishlist}
                                        parentProduct={parentProduct}
                                        showAddedCta={showAddedCta}
                                        setChildProductSelection={setChildProductSelection}
                                        childProductSelection={childProductSelection}
                                        isProductPartOfSet
                                        childOfBundleQuantity={quantity}
                                        countryISO={countryISO}
                                        selectedBundleParentQuantity={1} // hardcoded
                                        isProductLoading={isProductLoading}
                                        isBasketLoading={isBasketLoading}
                                        onFavouriteToggle={(
                                            toBeFavourite,
                                            selectedProductId,
                                            selectedProductName
                                        ) => {
                                            const action = toBeFavourite
                                                ? addItemToWishlist
                                                : removeItemFromWishlist
                                            return action(
                                                index,
                                                selectedProductId,
                                                selectedProductName
                                            )
                                        }}
                                    />
                                </SwiperSlide>
                            )
                        }
                    )}
                </Swiper>
            </Box>
        </Box>
    )
}

ComboChildProducts.getTemplateName = () => 'child-combo-products'

// PropTypes
ComboChildProducts.propTypes = {
    match: PropTypes.object,
    comboProduct: PropTypes.shape({
        childProducts: PropTypes.arrayOf(
            PropTypes.shape({
                product: PropTypes.object,
                quantity: PropTypes.number
            })
        )
    }),
    basket: PropTypes.object,
    showAddedCta: PropTypes.bool,
    countryISO: PropTypes.string,
    setChildProductSelection: PropTypes.any,
    childProductSelection: PropTypes.any,
    addToCart: PropTypes.func,
    wishlist: PropTypes.object,
    parentProduct: PropTypes.object,
    customer: PropTypes.object,
    isBasketLoading: PropTypes.bool,
    setIsChildLoading: PropTypes.func,
    isProductLoading: PropTypes.bool
}

export default ComboChildProducts
